import React, { useState, useEffect } from 'react';
import {
	PieChart,
	Pie,
	Cell,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	LineChart,
	Line,
	LabelList,
	ScatterChart,
	Scatter,
	ZAxis,
	AreaChart,
	Area,
	RadarChart,
	PolarGrid,
	PolarAngleAxis,
	PolarRadiusAxis,
	Radar,
} from 'recharts';
import moment from 'moment';
import TableLoading from '../../components/TableLoading';
import { formatCamelCaseToText, formatHyphenatedText, mapTimeRangeToApiFilterAlt3, request } from '../../services/utilities';
import { analyticsAPI, analyticsAPIV2 } from '../../services/constants';
import { count } from 'rxjs';
import { TimeRangeDropdownAlt3 } from '../../components/TimeRangeDropdownAlt3';
import { Statistic } from 'antd';
import BedOccupancyHeatmap from '../FrontDesk/Components/HeatMap';
// import AntenatalOverview from './AntenatalOverview';

const METRIC_LABEL_MAP = {
	total: 'Total Admissions',
	totalactive: 'Currently Admitted',
	discharged: 'Total Discharged',
};

const PRIORITY_METRIC_KEYS = ['total', 'totalactive', 'discharged'];

const normalizeMetricKey = key =>
	String(key || '')
		.toLowerCase()
		.replace(/[\s_-]/g, '');

const getMetricTitle = key => {
	const normalizedKey = normalizeMetricKey(key);
	return METRIC_LABEL_MAP[normalizedKey] || formatCamelCaseToText(key);
};

const isRoomNameMetric = key => ['roomname', 'room', 'name'].includes(normalizeMetricKey(key));

const mapTimeRangeToApiFilter = range => {
	switch (range) {
		case 'today':
			return 'today';
		case 'pastWeek':
			return 'past-week';
		case 'lastMonth':
			return 'last-month';
		case 'pastQuarter':
			return 'past-quarter';
		case 'pastSixMonths':
			return 'past-six-months';
		case 'pastThreeYears':
			return 'past-three-years';
		case 'past30Days':
			return 'past-month';
		case 'pastYear':
			return 'past-year';
		case 'lastYear':
			return 'last-year';
		case 'allTime':
			return 'all-time';
		default:
			return 'past-month';
	}
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ff6e54', '#ffc0cb', '#faad14'];

const AdmissionOverview = () => {
	const [timeRanges, setTimeRanges] = useState({
		nicuTrends: 'past30Days',
		count: 'past30Days',
		admissionMetrics: 'past30Days',
		occupancy: 'past30Days',
		admissionRates: 'past30Days',
		admissionTrends: 'past30Days',
		doctorAdmission: 'past30Days',
	});

	const [counts, setCounts] = useState([]);
	const [admissionMetrics, setAdmissionMetrics] = useState([]);
	const [occupancy, setOccupancy] = useState([]);
	const [admissionRates, setAdmissionRates] = useState([]);
	const [admissionTrends, setAdmissionTrends] = useState([]);
	const [doctorAdmission, setDoctorAdmission] = useState([]);

	const [loadingStates, setLoadingStates] = useState({
		count: false,
		admissionMetrics: false,
		occupancy: false,
		admissionRates: false,
		admissionTrends: false,
	});

	// count
	const fetchNicuCount = async range => {
		setLoadingStates(prev => ({ ...prev, nicuTrends: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/nicu-count?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result;
			setCounts(apiResponse ?? []);
		} catch (error) {
			console.error('Failed to fetch nicu counts:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, count: false }));
		}
	};

	const fetchAdmissionMetrics = async range => {
		setLoadingStates(prev => ({ ...prev, admissionMetrics: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);

		try {
			const res = await request(`${analyticsAPIV2}/admission/admission-metrics?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result;

			const formattedData = (apiResponse || []).map(item => ({
				categoryName: item?.categoryName || 'Not Assigned',
				roomName: item?.roomName || item?.room || item?.name || 'Not Assigned',
				data: Object.entries(item)
					.filter(([key]) => !['categoryName', 'roomName', 'room', 'name'].includes(key))
					.map(([key, value]) => ({
						name: key,
						value: Number(value) || 0,
					})),
			}));

			setAdmissionMetrics(formattedData);
		} catch (error) {
			console.error('Failed to fetch admission metrics:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, admissionMetrics: false }));
		}
	};

	const fetchAdmissionTrendsAlt = async range => {
		const dateFilter = mapTimeRangeToApiFilter(range);
		setLoadingStates(prev => ({ ...prev, admissionRates: true }));
		try {
			const res = await request(`analytics/admission-trends?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name:
					x?.date ||
					x?.day ||
					(dateFilter == 'past-three-years' && x?.month && moment(x?.month).format('MMMM,YY')) ||
					(x?.month && moment(x?.month).format('MMMM')) ||
					x?.week ||
					x?.year ||
					x?.hour,
				admissions: parseInt(x?.count, 10),
			}));
			setAdmissionRates(apiResponse);
		} catch (error) {
			console.error('Failed to fetch admission trends:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, admissionRates: false }));
		}
	};

	const fetchOccupancy = async () => {
		try {
			const res = await request(`analytics/room-count-admission`, 'GET', true);
			const formatted = (res?.result || []).map(item => ({
				name: `Bed ${item.number}`,
				occupancy: parseInt(item.count, 10),
			}));
			setOccupancy(formatted);
		} catch (error) {
			console.error('Failed to fetch occupancy:', error);
		}
	};

	const fetchDoctorAdmission = async range => {
		setLoadingStates(prev => ({ ...prev, doctorAdmission: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/doctor-admission?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = Object.entries(res?.result)
				// ?.sort(() => Math.random() - 0.5)
				?.map(([name, count]) => ({
					name,
					count,
				}))
				.slice(0, 30);

			setDoctorAdmission(apiResponse);
		} catch (error) {
			console.error('Failed to fetch data:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, doctorAdmission: false }));
		}
	};

	const fetchAdmissionTrends = async range => {
		setLoadingStates(prev => ({ ...prev, admissionTrends: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/doctor-admission-trend?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result?.map(x => ({
				name:
					x?.date ||
					(dateFilter == 'past-three-years' && x?.month && moment(x?.month).format('MMMM, YY')) ||
					(x?.month && moment(x?.month).format('MMMM')) ||
					x?.day ||
					x?.week ||
					x?.year ||
					x?.hour,
				count: parseInt(x?.count, 10),
			}));

			setAdmissionTrends(apiResponse);
		} catch (error) {
			console.error('Failed to fetch data:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, admissionTrends: false }));
		}
	};

	// Initial data load
	useEffect(() => {
		fetchNicuCount(timeRanges.count);
		fetchAdmissionMetrics(timeRanges.admissionMetrics);
		fetchOccupancy();
		fetchAdmissionTrendsAlt(timeRanges.admissionRates);
		fetchAdmissionTrends(timeRanges.admissionTrends);
		fetchDoctorAdmission(timeRanges.doctorAdmission);
	}, []);

	// Handle time range changes
	const handleTimeRangeChange = (range, chartId) => {
		setTimeRanges(prev => ({ ...prev, [chartId]: range }));

		switch (chartId) {
			case 'count':
				fetchNicuCount(range);
				fetchAdmissionMetrics(range);
				break;
			case 'admissionMetrics':
				fetchAdmissionMetrics(range);
				break;
			case 'occupancy':
				fetchOccupancy();
				break;
			case 'admissionRates':
				fetchAdmissionTrendsAlt(range);
				break;
			case 'doctorAdmission':
				fetchDoctorAdmission(range);
				break;
			case 'admissionTrends':
				fetchAdmissionTrends(range);
				break;
			default:
				break;
		}
	};

	const renderCustomLabelAlt = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value?.toLocaleString()}
			</text>
		);
	};

	const renderCustomLabelAlt2 = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value?.toLocaleString() + 'hr'}
			</text>
		);
	};

	const formatBarLabel = ({ x, y, value, width }) => (
		<text x={x + width / 2} y={y - 6} textAnchor="middle" fill="#333" fontSize={12}>
			{value?.toLocaleString()}
		</text>
	);

	const CustomToolTipAlt = ({ active, payload, label }) => {
		if (!active || !payload?.length) return null;

		const data = payload[0]?.payload;

		return (
			<div className="custom-tooltip" style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: 10 }}>
				<p>
					<strong>{String(label).charAt(0).toUpperCase() + String(label).slice(1)}</strong>
				</p>
				<p>Average Turn Around Time: {Number(data?.avgTATMinutes).toLocaleString() + 'mins'}</p>
			</div>
		);
	};

	console.log(admissionMetrics, '--------------------admission metrics');

	return (
		<div className="content-">
			<div className="content-bo">
				<div className="row">
					{/* Immunization */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header"> Admission Overview </h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges?.count} onChange={handleTimeRangeChange} chartId="count" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '13px', marginBottom: '10px' }}>
								Summary metrics grouped by room category for quick comparisons with less scrolling.
							</p>

							<div className="element-box">
								{loadingStates.count ? (
									<TableLoading />
								) : (
									<>
										{Object.entries(
											(admissionMetrics || []).reduce((acc, item) => {
												const category = item?.categoryName || 'Not Assigned';
												if (!acc[category]) acc[category] = [];
												acc[category].push(item);
												return acc;
											}, {})
										).map(([categoryName, rooms], categoryIndex) => (
											<div key={categoryName} className="mb-3">
												<div
													className="d-flex justify-content-between align-items-center"
													style={{
														padding: '8px 10px',
														borderRadius: '8px',
														background: '#f8fafc',
														borderLeft: `4px solid ${COLORS[categoryIndex % COLORS.length]}`,
														marginBottom: '10px',
													}}
												>
													<h6 className="mb-0" style={{ fontSize: '14px', fontWeight: 700 }}>
														{categoryName}
													</h6>
													<small className="text-muted">
														{rooms.length} room{rooms.length > 1 ? 's' : ''}
													</small>
												</div>

												<div className="row">
													{rooms.map((room, roomIndex) => {
														const roomName = room?.categoryName === 'Not Assigned' ? '' : room?.roomName;
														const metricData = (room?.data || []).filter(item => !isRoomNameMetric(item?.name));

														const totalItem = metricData.find(item => normalizeMetricKey(item?.name) === 'total');
														const totalValue = Number(totalItem?.value) || 0;

														const orderedMetrics = [...metricData].sort((a, b) => {
															const aPriority = PRIORITY_METRIC_KEYS.indexOf(normalizeMetricKey(a?.name));
															const bPriority = PRIORITY_METRIC_KEYS.indexOf(normalizeMetricKey(b?.name));
															const aWeight = aPriority === -1 ? 99 : aPriority;
															const bWeight = bPriority === -1 ? 99 : bPriority;
															return aWeight - bWeight;
														});

														return (
															<div key={`${categoryName}-${roomName || roomIndex}`} className="col-xl-6 col-lg-6 col-12 mb-3">
																<div
																	className="element-box"
																	style={{
																		borderTop: `4px solid ${COLORS[(categoryIndex + roomIndex) % COLORS.length]}`,
																		padding: '12px 14px',
																		minHeight: '100%',
																	}}
																>
																	<div className="d-flex justify-content-between align-items-center mb-2">
																		<h6 className="mb-0" style={{ fontSize: '14px', fontWeight: 700 }}>
																			{roomName || categoryName}
																		</h6>
																		<span
																			style={{
																				fontSize: '12px',
																				padding: '2px 8px',
																				borderRadius: '999px',
																				background: '#f5f7fb',
																				color: '#4a5568',
																			}}
																		>
																			{formatHyphenatedText(mapTimeRangeToApiFilterAlt3(timeRanges?.count))}
																		</span>
																	</div>

																	<div className="row" style={{ rowGap: '8px' }}>
																		{orderedMetrics.map((item, metricIndex) => {
																			const key = normalizeMetricKey(item?.name);
																			const color = COLORS[(categoryIndex + roomIndex + metricIndex) % COLORS.length];
																			const percentage =
																				key === 'total' || totalValue === 0
																					? null
																					: ((Number(item?.value || 0) / totalValue) * 100).toFixed(1);

																			return (
																				<div key={item?.name} className="col-12 col-md-6">
																					<div
																						style={{
																							padding: '8px 10px',
																							borderRadius: '8px',
																							background: '#f9fafb',
																							border: '1px solid #edf2f7',
																						}}
																					>
																						<Statistic
																							title={getMetricTitle(item?.name)}
																							value={Number(item?.value) || 0}
																							valueStyle={{
																								fontWeight: 'bold',
																								fontSize: '17px',
																								color,
																							}}
																						/>
																						{percentage && <small className="text-muted">{percentage}% of total</small>}
																					</div>
																				</div>
																			);
																		})}
																	</div>
																</div>
															</div>
														);
													})}
												</div>
											</div>
										))}
									</>
								)}
							</div>
						</div>
					</div>

					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Admission Rates</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges.admissionRates} onChange={handleTimeRangeChange} chartId="admissionRates" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart tracks hospital's active admissions for every date/ time range over time, indicating demand for in-patient
								services.
							</p>
							<div className="element-box">
								{loadingStates.admissionRates ? (
									<TableLoading />
								) : admissionRates.length > 0 ? (
									<div style={{ width: '100%', height: 300 }}>
										<ResponsiveContainer>
											<AreaChart data={admissionRates} margin={{ top: 20, right: 20, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.admissionRates == 'pastQuarter' ? 2 : 0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-40px' }} />
												<Area type="monotone" dataKey="admissions" stroke="#8884d8" fill="#8884d8" name="Admissions">
													<LabelList
														dataKey="admissions"
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
														formatter={value => `${value}`}
														content={renderCustomLabelAlt}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* doctor admission */}

					<div className="col-sm-12">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header">Doctor Admission Overview</h6>
								<TimeRangeDropdownAlt3 value={timeRanges.doctorAdmission} onChange={handleTimeRangeChange} chartId="doctorAdmission" />
							</div>
							<p className="chart-description card p-2">
								Comprehensive analytics on doctor productivity, patient loads, and clinical operations.
							</p>
							<div className="element-box">
								{loadingStates.doctorAdmission ? (
									<TableLoading />
								) : doctorAdmission.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<AreaChart data={doctorAdmission} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												{/* <Legend /> */}
												<Area
													type="monotone"
													dataKey="count"
													stroke="#0088FE"
													fill="#0088FE"
													strokeWidth={2}
													dot={{ r: 4 }}
													activeDot={{ r: 6 }}
												>
													<LabelList
														dataKey="count"
														content={renderCustomLabelAlt}
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/*doctor admission trend  */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header">Admission Trends</h6>
								<TimeRangeDropdownAlt3 value={timeRanges?.admissionTrends} onChange={handleTimeRangeChange} chartId="admissionTrends" />
							</div>
							<p className="chart-description card p-2">
								Comprehensive analytics on doctor productivity, patient loads, and clinical operations.
							</p>
							<div className="element-box">
								{loadingStates?.admissionTrends ? (
									<TableLoading />
								) : admissionTrends.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<BarChart data={admissionTrends} margin={{ top: 20, right: 20, left: 20, bottom: 45 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.admissionTrends === 'pastQuarter' ? 2 : 0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 15) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												{/* <Legend wrapperStyle={{ marginBottom: '-50px' }} /> */}
												<Bar dataKey="count">
													{admissionTrends.map((entry, index) => (
														<Cell key={`cell-eff-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />
													))}
													<LabelList dataKey="count" content={formatBarLabel} position="top" style={{ fontSize: '12px', fill: '#333' }} />
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* Bed Occupancy  */}
					{/* <div className="row"> */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Bed usage </h6>

							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart shows an all time bed usage rates, with colors indicating low (green), medium (yellow), and high (red).
							</p>
							<div className="element-box">
								{loadingStates.bedOccupancy ? (
									<TableLoading />
								) : occupancy.length > 0 ? (
									<div style={{ width: '100%', height: 300 }}>
										<BedOccupancyHeatmap data={occupancy} />
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>
					{/* </div> */}
				</div>
			</div>
		</div>
	);
};

export default AdmissionOverview;
